import { Link } from "gatsby"
import React from "react"
import { Hidden, Visible } from "react-grid-system"
import styled from "styled-components"
import { ButtonLink } from "styleguide/components"
import { Col, Row } from "styleguide/components/grid"
import { IconGithubSmall, IconReactOnRailsLogo } from "styleguide/icons"
import { uiColorToBaseColor } from "styleguide/styles/colors"
import media from "styleguide/styles/media-queries"
import { Articles, Location } from "../../../types/docs"
import { SearchTrigger } from "../../../components/SearchTrigger"
import Menu from "./Menu"
import MobileMenu from "./MobileMenu"

interface Props {
  repoName: string
  repoDisplayName: string
  mdxName?: string
  currentSlug?: string
  categories?: string[]
  location?: Location
  articles?: Articles
}

const StyledHeader = styled.div`
  width: 100%;
  position: fixed;
  z-index: 100;
  background: #fff;
`

const LeftCol = styled(Col)`
  display: flex;
  align-items: center;
`

const RightCol = styled(Col)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  max-width: 250px;

  ${media.tablet`
    gap: 8px;
  `}
`

const StyledContainer = styled.div`
  padding: 16px 32px;
  border-bottom: 1px solid ${uiColorToBaseColor("lightGray")};

  ${media.tablet`
    padding-left: 16px !important;
    padding-right: 16px !important;
  `}
`

const StyledLogo = styled(IconReactOnRailsLogo)`
  width: 48px;
  height: 48px;
`

const LogoName = styled.div`
  font-size: 18px;
  line-height: 24px;
  margin-left: 12px;
  color: ${uiColorToBaseColor("black")};
`

const StyledLogoLink = styled(Link)`
  display: flex;
  align-items: center;
  padding-right: 18px;
  border-right: 1px solid ${uiColorToBaseColor("lightGray")};

  ${media.desktop`
    border-right: none;
   `}

  &:hover {
    text-decoration: none;
  }
`

const StyledButtonLink = styled(ButtonLink)`
  background-color: ${uiColorToBaseColor("white")};
  color: ${uiColorToBaseColor("black")};
  display: flex;
  align-items: center;
  border: 1px solid ${uiColorToBaseColor("black")};
  font-size: 14px;
  line-height: 24px;
  padding: 5px 20px;
  white-space: nowrap;
  font-weight: 600;

  &:hover {
    background-color: ${uiColorToBaseColor("white")};
    color: ${uiColorToBaseColor("black")};
  }
`

const StyledIconGithubSmall = styled(IconGithubSmall)`
  max-width: 24px;
  width: 24px;
  margin-right: 12px;
  height: 25px;
  margin-top: 5px;

  ${media.tablet`
    margin-right: 0;
  `}
`

const StyledMobileMenu = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`

const repoLink = (repoName: string) => {
  if (repoName === "control-plane-flow") {
    return `https://github.com/shakacode/${repoName}`
  }

  if (repoName === "react-on-rails") {
    return "https://github.com/shakacode/react_on_rails"
  }

  return ""
}

const Header = ({
  repoName,
  repoDisplayName,
  mdxName,
  currentSlug,
  categories,
  location,
  articles,
}: Props) => {
  const githubUrl = repoLink(repoName)
  return (
    <StyledHeader>
      <StyledContainer>
        <Row>
          <LeftCol xs={9} sm={6} md={5} lg={8}>
            <StyledLogoLink to={`/${repoName}/docs/`}>
              <StyledLogo />
              <LogoName>{repoDisplayName}</LogoName>
            </StyledLogoLink>
            <Hidden md sm xs>
              <Menu repoName={repoName} />
            </Hidden>
          </LeftCol>
          <RightCol xs={3} sm={6} md={7} lg={4}>
            <SearchTrigger />
            <Visible md lg xl>
              {githubUrl && (
                <StyledButtonLink href={githubUrl} target="_blank">
                  <StyledIconGithubSmall color="black" />
                  <Visible xl>
                    <span>View on Github</span>
                  </Visible>
                  <Visible lg md>
                    <span>Github</span>
                  </Visible>
                </StyledButtonLink>
              )}
            </Visible>
            <Hidden lg xl>
              <StyledMobileMenu>
                <MobileMenu
                  repoName={repoName}
                  repoLink={githubUrl}
                  mdxName={mdxName}
                  currentSlug={currentSlug}
                  categories={categories}
                  location={location}
                  articles={articles}
                />
              </StyledMobileMenu>
            </Hidden>
          </RightCol>
        </Row>
      </StyledContainer>
    </StyledHeader>
  )
}
export default Header
